import { computed, nextTick } from 'vue';
import {
  getAssetContainerStatusIcon,
  getAssetContainerStatusColor,
  getAssetContainerStateIcon
} from '@satellite/helpers/yardTaskUtils';
import { useRoute, useRouter } from 'vue-router';
import { isUUID, isDefined } from 'class-validator';
import { useStore } from 'vuex';
import { camelCase, get, last, trim, isString, debounce } from 'lodash';
import { DateTime } from 'luxon';
import { useAuth, useMixpanel, useEventHub, useCustomForms, useNotify } from '@/composables';
import {
  AssetVisitEventType,
  AssetVisitType,
  SpotType,
  SPOT_STATUSES_RESERVED,
  SpotStatus,
  getSpotStatus,
  LuxonDateTimeFormats,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  isSpotAssignedOnlyToAssetContainer,
  isSpotAssignedOnlyToAssetVisit,
  getAssetContainerStatus,
  isAssetVisitCancelled,
  isAssetVisitDeparted,
  SpotEventTypeEnum,
  canUserCreateSpotAssignment,
  formatDateTimeWithMilitarySupport,
  YARD_SPOT_CODE_MAX_LENGTH,
  AppointmentStatus,
  sortSpots,
  LoadTypeDirection,
  AssetContainerEventType,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum,
  YardViewModeEnum,
  canUserCreateSpot,
  isUserRoleGreaterOrEqual,
  UserRole,
  CustomFieldType,
  isUserRoleLessOrEqual,
  getDirectionForVisitType,
  AssetVisitDirection,
  isLiveAssetVisitType,
  NovaDate
} from '@nova/core';
import yardSpotService from '@satellite/services/yardSpot-service';
import { getAssetVisitFetchWindowStart } from '@satellite/services/asset-visit-service';
import { useI18n } from 'vue-i18n';

/**
 * Mirrors the initial fetch's "last N days" window (see getLatestAssetVisitsByWarehouseId).
 * Returns true when an asset visit is recent enough to belong in the waiting queue. Payloads with a
 * missing or unparseable createDateTime (e.g. truncated oversized Subspace rows) are NOT dropped —
 * we cannot prove they are stale. (OD-3730)
 * @param {{ createDateTime?: string | Date }} entity
 * @returns {boolean}
 */
export function isAssetVisitWithinFetchWindow(entity) {
  const createDateTime = entity?.createDateTime;
  if (!createDateTime) return true;
  const dt = new NovaDate(createDateTime);
  if (!dt.isValid()) return true;
  return dt.toDateTime() >= getAssetVisitFetchWindowStart();
}

/** Key of the column after which custom (asset detail) columns are rendered in the list view. */
export const CUSTOM_COLUMNS_AFTER_KEY = 'spotCode';

/**
 * Returns standard columns with custom columns inserted immediately after the anchor column.
 * Used by list view (header order) and column select (dropdown order).
 */
export function placeCustomColumnsAfterAnchor(
  standardColumns,
  customColumns,
  anchorKey = CUSTOM_COLUMNS_AFTER_KEY
) {
  const anchorIndex = standardColumns.findIndex(c => c.key === anchorKey);
  if (anchorIndex === -1 || !customColumns?.length) {
    return [...standardColumns, ...(customColumns || [])];
  }
  return [
    ...standardColumns.slice(0, anchorIndex + 1),
    ...customColumns,
    ...standardColumns.slice(anchorIndex + 1)
  ];
}

export default function useYard() {
  const { t, locale } = useI18n();
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const mixpanel = useMixpanel();
  const eventHub = useEventHub();
  const { getFormattedValue } = useCustomForms();
  const notify = useNotify();

  // Event payload: { assetVisit: Object, newSpotCode: string, newSpotAreaName: string }
  const SPOT_ASSIGNMENT_CHANGED = 'spot-assignment-changed';

  // Event payload: { newSpotCode: string }
  const GO_TO_SPOT = 'go-to-spot';

  // Event payload: void (signals a new check-in arrived via websocket)
  const NEW_WAITING_ARRIVAL = 'new-waiting-arrival';

  const storePath = 'state.Yard';
  const spotCardWidth = 60;
  const spotCardHeight = 170;
  const getSpotIcon = type => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  const getTruckIcon = type => {
    if (type === AssetVisitType.PickUp) {
      return 'mdi-truck-flatbed';
    }

    if (type === AssetVisitType.Drop || type === AssetVisitType.DropHook) {
      return 'mdi-truck-cargo-container';
    }

    return 'mdi-truck-outline';
  };

  const isYMSEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.YMS)
  );

  const isGateManagementEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.GateManagement)
  );

  const getAppointmentSpot = appointmentId => {
    if (appointmentId) {
      const spot = spots.value.find(spot =>
        spot.spotAssignments.find(sa => sa.assetVisit?.appointmentId === appointmentId)
      );

      if (spot) {
        spot.spotArea = spotAreas.value.find(sa => sa.id === spot.spotAreaId);

        return spot;
      }
    }

    return null;
  };

  const getAssetVisitSpot = assetVisitId => {
    if (assetVisitId) {
      const spot = spots.value.find(spot =>
        spot.spotAssignments.find(sa => sa.assetVisitId === assetVisitId)
      );

      if (spot) {
        spot.spotArea = spotAreas.value.find(sa => sa.id === spot.spotAreaId);
        return spot;
      }
    }
    return null;
  };

  const getArrivedAssetContainerCompanyName = assetContainer => {
    // If attached to a Live visit, don't show dropoff carrier (carrier stays with the truck)
    if (assetContainer?.assetVisitId) {
      const parentVisit = assetVisits.value?.find(av => av.id === assetContainer.assetVisitId);
      if (parentVisit && isLiveAssetVisitType(parentVisit.visitType)) {
        return ' - ';
      }
    }

    // 1. First priority: dropAppointment (when asset has an appointment)
    if (assetContainer?.dropAppointment?.id) {
      return assetContainer.dropAppointment?.user?.company?.name || ' - ';
    }

    // 2. Fallback: use carrier from 'Arrived' event (for unplanned check-ins)
    const arrivedEvent = getArrivedAssetContainerEvent(assetContainer);
    if (arrivedEvent?.assetVisit?.id) {
      return arrivedEvent.assetVisit.company?.name || arrivedEvent.assetVisit.companyHint || ' - ';
    }

    // 3. Fallback: event's assetVisit relation not loaded (nested containers),
    //    look up the original drop-off visit from the store
    if (arrivedEvent?.assetVisitId) {
      const visit = assetVisits.value?.find(av => av.id === arrivedEvent.assetVisitId);
      if (visit) {
        return visit.company?.name || visit.companyHint || ' - ';
      }
    }

    return ' - ';
  };

  const getArrivedAssetContainerEvent = assetContainer => {
    if (!assetContainer?.assetContainerEvents?.length) {
      return ' - ';
    }

    return assetContainer.assetContainerEvents.find(
      e => e?.eventType === AssetContainerEventType.Arrived
    );
  };

  /* Get the value of a field from both primary and secondary appointment, if available
   * and join them with a comma
   */
  const getAppointmentFieldValue = (assetVisit, fieldName) => {
    const values = [
      assetVisit?.appointment?.[fieldName],
      assetVisit?.secondaryAppointment?.[fieldName]
    ].filter(v => isString(v) && trim(v) !== '');

    return values.length ? values.map(trim).join(', ') : null;
  };

  const formatHour = value =>
    formatDateTime(
      value,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      LuxonDateTimeFormats.Extended24HrTime
    );

  const scheduledFor = appointment => {
    try {
      return Boolean(selectedWarehouse.value?.id) &&
        Boolean(appointment?.id) &&
        Boolean(appointment?.start) &&
        Boolean(appointment?.end)
        ? {
            date: formatDateTime(
              appointment?.start,
              LuxonDateTimeFormats.DateWithAbbreviatedMonth,
              LuxonDateTimeFormats.DateWithAbbreviatedMonth
            ),
            start: formatHour(appointment?.start),
            end: formatHour(appointment?.end)
          }
        : {};
    } catch (error) {
      console.warn('Error while formatting appointment', error);
      return {};
    }
  };

  const getAssignedAssetContainerStatus = assetContainerEvents =>
    getAssetContainerStatus(assetContainerEvents);

  const isAssetContainer = asset =>
    asset && isString(asset.code) && Boolean(asset.type) && !Object.hasOwn(asset, 'visitType');

  const getAssignedSpotIcon = loadtypeDirection => {
    if (loadtypeDirection) {
      if (loadtypeDirection === LoadTypeDirection.Inbound) {
        return 'mdi-menu-up';
      }

      if (loadtypeDirection === LoadTypeDirection.Outbound) {
        return 'mdi-menu-down';
      }

      if (loadtypeDirection === LoadTypeDirection.InOut) {
        return 'mdi-menu-swap';
      }
    }

    return null;
  };

  const getLoadTypeDirectionColor = loadTypeDirection => {
    if (loadTypeDirection === LoadTypeDirection.Inbound) {
      return 'info';
    }
    if (loadTypeDirection === LoadTypeDirection.Outbound) {
      return 'warning';
    }

    return 'text--color-black';
  };

  const relativeTimeFromWarehouse = date => {
    const zone = selectedWarehouse.value.timezone;
    return DateTime.fromISO(date, { zone })
      .setLocale(locale.value)
      .toRelative({ base: DateTime.now().setZone(zone) })
      .replace('ago', '');
  };

  const timeElapsedFromWarehouse = date => {
    if (!date) return '';

    const zone = selectedWarehouse.value.timezone;
    const start = DateTime.fromISO(date, { zone });
    const now = DateTime.now().setZone(zone);

    const diff = now.diff(start);
    if (diff.as('minutes') < 1) {
      return '00:01';
    }
    return diff.toFormat('hh:mm');
  };

  const MixPanelEvent = Object.freeze({
    VIEW_PAGE: 'Viewed: Page',
    CREATE_ASSET_VISIT: 'Created: Asset Visit',
    CREATE_ASSET_CONTAINER: 'Created: Asset Container',
    UPDATE_ASSET_CONTAINER_STATUS: 'Updated: Asset Container Status',
    UPDATE_ASSET_CONTAINER: 'Updated: Asset Container',
    LINK_ASSET_VISIT_APPOINTMENT: 'Linked: Asset Visit Appointment',
    UNLINK_ASSET_VISIT_APPOINTMENT: 'Unlinked: Asset Visit Appointment',
    UNLINK_ASSET_CONTAINER_APPOINTMENT: 'Unlinked: Asset Container Appointment',
    LINK_ASSET_CONTAINER_APPOINTMENT: 'Linked: Asset Container Appointment',
    GO_TO_SPOT: 'Clicked: Go to Spot',
    DROP: 'Dropped: Asset Container',
    HOOK: 'Hooked: Asset Container',
    DEPART: 'Departed: Asset Visit',
    ASSIGNED: 'Assigned: Asset to Spot',
    REASSIGN: 'Reassigned: Asset to Spot',
    EXPORT: 'Exported: Yard Assets'
  });

  /*
   * Track mixpanel event for YMS module
   * @param {String} event - Mixpanel event name, check mixPanelEvent for available events
   * @param {Object} spot - Spot object
   * @param {Object} assetVisit - AssetVisit object
   * @param {String} entryPoint - Entry point
   * @param {Object} metadata - Additional metadata, as object
   * @returns {void}
   * */
  const trackMixpanelEvent = (event, { spot, assetVisit, entryPoint, spotArea, metadata }) => {
    try {
      const loggedUser = store.getters['Auth/me'];
      const localSpotArea = spotAreas.value.find(sa => sa.id === spot?.spotAreaId);
      const eventData = {
        org_id: loggedUser?.orgId,
        org_name: loggedUser?.orgName,
        warehouse_id: selectedWarehouse.value?.id,
        warehouse_name: selectedWarehouse.value?.name,
        area_id: (spotArea ?? localSpotArea)?.id,
        area_name: (spotArea ?? localSpotArea)?.name,
        spot_id: spot?.id,
        spot_code: spot?.code,
        spot_type: spot?.type,
        asset_visit_id: assetVisit && !isAssetContainer(assetVisit) ? assetVisit?.id : null,
        asset_visit_type: assetVisit?.visitType,
        asset_container_id: assetVisit && isAssetContainer(assetVisit) ? assetVisit?.id : null,
        asset_container_type: assetVisit && isAssetContainer(assetVisit) ? assetVisit?.type : null,
        checkin_type: assetVisit
          ? assetVisit?.isPlanned
            ? 'Planned Arrival'
            : 'Unplanned Arrival'
          : null,
        entry_point: entryPoint,
        // eslint-disable-next-line no-unused-vars
        ...(metadata && Object.fromEntries(Object.entries(metadata).filter(([_, v]) => v != null)))
      };

      mixpanel.track(
        event,
        // eslint-disable-next-line no-unused-vars
        Object.fromEntries(Object.entries(eventData).filter(([_, v]) => v != null))
      );
    } catch (err) {
      console.warn(`Failed to track mixpanel event ${event}`, err);
    }
  };

  const goToSpotFromOtherModules = async ({ spotId, spotCode, warehouseId }) => {
    if (!spotId || !spotCode || !warehouseId) {
      throw new Error('Invalid parameters for goToSpotFromOtherModules');
    }

    return router
      .push({
        name: 'yard-warehouse',
        params: {
          warehouseId
        }
      })
      .then(() => {
        // Wait for spots and visits to load before emitting the event
        let maxChecks = 5;
        const { $emit } = useEventHub();
        const checkLoading = () => {
          if (maxChecks-- <= 0) {
            throw new Error('Timeout waiting for spots and visits to load');
          }

          if (
            !isLoadingSpots.value &&
            !isLoadingAssetVisits.value &&
            selectedAreas.value.length > 0 &&
            spots.value.length > 0
          ) {
            const spot = spots.value.find(spot => spot.id === spotId);
            selectedSpot.value = spot;

            viewMode.value = YardViewModeEnum.GRID;

            trackMixpanelEvent(MixPanelEvent.GO_TO_SPOT, {
              spot: { id: spotId, code: spotCode },
              entryPoint: 'Appointment Details'
            });

            $emit(GO_TO_SPOT, {
              newSpotCode: spotCode
            });
          } else {
            setTimeout(checkLoading, 400);
          }
        };
        checkLoading();
      });
  };

  const truckThemeColors = {
    blue: {
      backgroundColor: '#E4F1F7',
      borderColor: '#005AD8'
    },
    red: {
      backgroundColor: '#FAEBEE',
      borderColor: '#D40047'
    },
    grey: {
      backgroundColor: '#F2F2F2',
      borderColor: '#656565'
    }
  };

  const getTruckThemeColor = assetVisit => {
    const appointment = assetVisit?.appointment;
    const warehouseNow = DateTime.now().setZone(selectedWarehouse.value.timezone);

    if (appointment?.id) {
      if (
        appointment.status == AppointmentStatus.Cancelled ||
        appointment.status == AppointmentStatus.NoShow ||
        // If appointment end time is in the past, we should consider it as cancelled
        warehouseNow > DateTime.fromISO(appointment.end)
      ) {
        return { ...truckThemeColors.red, theme: 'red' };
      }

      // If it is an active appointment and not ended yet, show as blue
      return { ...truckThemeColors.blue, theme: 'blue' };
    }

    // When no appointment, show as grey
    return { ...truckThemeColors.grey, theme: 'grey' };
  };

  const isEditableSpotSelected = spot => {
    if (selectedSpots?.value?.length >= 1) {
      return Boolean(selectedSpots.value.find(s => s.id === spot.id)?.id);
    }

    return selectedSpot.value?.id === spot.id;
  };

  const isMilitaryTimeEnabled = computed(() =>
    store.getters['Settings/isMilitaryTimeEnabled']({
      ...selectedWarehouse.value
    })
  );

  const getEvents = async spotId => {
    const events = await yardSpotService.getEvents(spotId);

    return Array.isArray(events) ? events : [];
  };

  const getLatestClosedEvent = async spotId => {
    // TODO: Filter by eventType perhaps and sort by createdAt
    const events = await getEvents(spotId);

    if (events.length > 0) {
      const closeEvents = events
        .filter(event => event.eventType === SpotEventTypeEnum.CLOSE)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return last(closeEvents) || {};
    }

    return {};
  };

  const getContainerSpotAssignmentText = assetContainerId => {
    const spot = spots.value.find(spot =>
      spot.spotAssignments?.find(
        sa =>
          sa.assetContainerId === assetContainerId ||
          sa.assetVisit?.assetContainers?.find(ac => ac.id === assetContainerId)
      )
    );

    if (!spot) {
      return {
        spotAreaName: null,
        spotCode: null,
        spotId: null
      };
    }

    const spotArea = spotsByAreaMap.value.get(spot.spotAreaId)?.area ?? null;

    return {
      spotAreaName: spotArea?.name ?? spot.spotAreaName ?? null,
      spotCode: spot.code ?? null,
      spotId: spot.id ?? null
    };
  };

  const visitTypeToI18nKey = {
    [AssetVisitType.Live]: 'live',
    [AssetVisitType.LiveUnload]: 'live',
    [AssetVisitType.LiveLoad]: 'live',
    [AssetVisitType.Drop]: 'drop',
    [AssetVisitType.PickUp]: 'pickup',
    [AssetVisitType.DropHook]: 'dropHook'
  };

  const directionToI18nKey = {
    [AssetVisitDirection.Inbound]: 'inbound',
    [AssetVisitDirection.Outbound]: 'outbound',
    [AssetVisitDirection.InboundAndOutbound]: 'inboundAndOutbound'
  };

  const getAssetVisitTypeDisplayName = type => {
    const typeKey = visitTypeToI18nKey[type];
    if (!typeKey) return type;

    const typeName = t(`core.yard.assetVisitTypes.${typeKey}`);
    const direction = getDirectionForVisitType(type);
    if (!direction) return typeName;

    const dirKey = directionToI18nKey[direction];
    const directionName = t(`core.yard.assetVisitDirections.${dirKey}`);
    return `${typeName} · ${directionName}`;
  };

  const isLoadingSpots = computed(() => get(store, `${storePath}.isLoadingSpots`));
  const isLoadingAssetVisits = computed(() => get(store, `${storePath}.isLoadingAssetVisits`));
  const assetContainerTriggers = computed(() => get(store, `${storePath}.assetContainerTriggers`));

  const spotAreas = computed(() => get(store, `${storePath}.spotAreas`));
  const spots = computed(() => get(store, `${storePath}.spots`));
  const assetVisits = computed(() => get(store, `${storePath}.assetVisits`));
  const assetContainers = computed(() => get(store, `${storePath}.assetContainers`));

  const waitingAssetVisits = computed(() => {
    // Filter out departed/canceled but NOT assigned — assignment status is
    // determined reactively from spots.value below, not from the entity's own
    // spotAssignments (which can be stale after optimistic updates).
    const validAssetVisits = (assetVisits.value || []).filter(
      av => !isAssetVisitCancelled(av) && !isAssetVisitDeparted(av)
    );
    const assignedAssetVisitIds = new Set(
      spots.value
        .filter(s => s?.spotAssignments?.length > 0)
        .flatMap(s => [
          s.spotAssignments[0]?.assetContainerId,
          s.spotAssignments[0]?.assetVisitId,
          ...(s.spotAssignments[0]?.assetVisit?.assetContainers?.map(ac => ac.id) || [])
        ])
    );

    const notAssigned = validAssetVisits.filter(av => !assignedAssetVisitIds.has(av.id));

    return notAssigned;
  });

  // Read-write refs
  const selectedWarehouse = computed({
    get() {
      return get(store, `${storePath}.selectedWarehouse`);
    },
    set(value) {
      store.commit('Yard/setSelectedWarehouse', value || null);

      if (value?.id) {
        // Make sure all related data is fetched
        fetchAssetVisits();
        fetchSpotAreas();
        fetchSpots();
        fetchAssetContainerTriggers();

        // Update warehouseId to the url
        router.push({
          params: { warehouseId: value.id }
        });
      } else {
        // Clean up the url
        router.push({
          name: 'yard'
        });
      }
    }
  });
  const selectedAreas = computed({
    get() {
      return get(store, `${storePath}.selectedAreas`);
    },
    set(value) {
      store.commit('Yard/setSelectedAreas', value || []);
    }
  });
  const selectedSpot = computed({
    get() {
      return get(store, `${storePath}.selectedSpot`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpot', value || {});
      if (value?.id) {
        store
          .dispatch('Yard/fetchSpot', value.id)
          .then(spot => store.commit('Yard/setSelectedSpot', spot));
        selectedSpots.value = [];

        nextTick(() => {
          const element = document.getElementById(value.id);

          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }, 500);
          }
        });
      }
    }
  });
  const selectedAssetVisit = computed({
    get() {
      return get(store, `${storePath}.selectedAssetVisit`);
    },
    set(value) {
      store.commit('Yard/setSelectedAssetVisit', value || null);
    }
  });
  const isAssetVisitChatVisible = computed({
    get() {
      return get(store, `${storePath}.isAssetVisitChatVisible`);
    },
    set(value) {
      store.commit('Yard/setIsAssetVisitChatVisible', value);
    }
  });
  const selectedSpots = computed({
    get() {
      return get(store, `${storePath}.selectedSpots`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpots', value || []);
      if (value?.length > 0) {
        selectedSpot.value = {};
      }
    }
  });
  const viewMode = computed({
    get() {
      return get(store, `${storePath}.viewMode`);
    },
    set(value) {
      store.dispatch('Yard/setViewMode', { value });
    }
  });

  // Route refs
  const defaultWarehouseId = computed(() =>
    isUUID(route.params?.warehouseId) ? String(route.params.warehouseId) : null
  );
  const defaultSpotAreaId = computed(() =>
    isUUID(route.params?.spotAreaId) ? String(route.params.spotAreaId) : null
  );
  const defaultSpotId = computed(() =>
    isUUID(route.params?.spotId) ? String(route.params.spotId) : null
  );

  const dropHandled = computed(() => get(store, `${storePath}.dropHandled`));
  const setDropHandled = value => store.commit('Yard/setDropHandled', Boolean(value));
  const dragOriginSpotIdRef = computed(() => get(store, `${storePath}.dragOriginSpotId`));
  const setDragOriginSpotId = value =>
    store.commit('Yard/setDragOriginSpotId', value == null ? null : String(value));

  const auth = useAuth();

  const cannotCreateSpotAssignment = computed(() => {
    const user = auth.meRef.value;
    return !canUserCreateSpotAssignment(user);
  });

  const canManageSpotsAndAreas = computed(() => {
    const user = auth.meRef.value;
    return canUserCreateSpot(user);
  });

  const canEditAssetForms = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
  });

  const canEditAssetContainerData = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT);
  });

  const isReadonlyAccess = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleLessOrEqual(user.role, UserRole.SPECTATOR);
  });

  // Actions
  const fakeCheckIn = data => store.dispatch('Yard/fakeCheckIn', data);
  const fakeCheckInUnplanned = data => store.dispatch('Yard/fakeCheckInUnplanned', data);
  const fetchPreviousYardData = () => store.dispatch('Yard/fetchPreviousYardData');
  const persistSelectedWarehouse = warehouseId =>
    store.dispatch('Yard/persistSelectedWarehouse', warehouseId);
  const persistListViewColumns = columns => store.dispatch('Yard/persistListViewColumns', columns);
  const persistListViewCustomColumns = columns =>
    store.dispatch('Yard/persistListViewCustomColumns', columns);
  const persistWaitingSortOrder = sortOrder =>
    store.dispatch('Yard/persistWaitingSortOrder', sortOrder);
  const persistShowWaitingAssets = value => store.dispatch('Yard/persistShowWaitingAssets', value);
  const persistSelectedAreas = areas => store.dispatch('Yard/persistSelectedAreas', areas);
  const persistExpandedAreaPanels = expandedIds =>
    store.dispatch('Yard/persistExpandedAreaPanels', expandedIds);
  const detachAssetVisit = (
    assetVisitId,
    assetVisitAction,
    assetContainerActions,
    reassignData = null
  ) =>
    store.dispatch('Yard/detachAssetVisit', {
      assetVisitId,
      assetVisitAction,
      assetContainerActions,
      reassignData
    });
  const attachAssetVisit = (assetVisitId, assetContainerActions) =>
    store.dispatch('Yard/attachAssetVisit', { assetVisitId, assetContainerActions });
  const updateAssetContainer = (assetContainerId, data) =>
    store.dispatch('Yard/updateAssetContainer', { assetContainerId, data });
  const updateAssetContainerStatus = (assetContainerId, eventType) =>
    store.dispatch('Yard/updateAssetContainerStatus', { assetContainerId, eventType });
  const updateAssetVisitStatus = (assetVisitId, eventType) =>
    store.dispatch('Yard/updateAssetVisitStatus', { assetVisitId, eventType });
  const getAppointmentById = appointmentId =>
    store.dispatch('Appointments/getAppointmentById', appointmentId);
  const fetchAssetVisits = () => store.dispatch('Yard/fetchAssetVisits');
  const fetchAndUpsertAssetVisit = id => store.dispatch('Yard/fetchAndUpsertAssetVisit', id);
  const fetchAndUpsertAssetContainer = id =>
    store.dispatch('Yard/fetchAndUpsertAssetContainer', id);
  const fetchAssetContainers = () => store.dispatch('Yard/fetchAssetContainers');
  const fetchAssetContainerTriggers = () => store.dispatch('Yard/fetchAssetContainerTriggers');
  const fetchSpots = () => store.dispatch('Yard/fetchSpots');
  const fetchSpotAreas = () => store.dispatch('Yard/fetchSpotAreas');
  const fetchSpot = id => store.dispatch('Yard/fetchSpot', id);
  const createSpotArea = data => store.dispatch('Yard/createSpotArea', data);
  const updateSpotArea = (id, data) => store.dispatch('Yard/updateSpotArea', { id, data });
  const deleteSpotArea = id => store.dispatch('Yard/deleteSpotArea', id);
  const updateSpot = (id, data) => store.dispatch('Yard/updateSpot', { id, data });
  const createSpot = data => store.dispatch('Yard/createSpot', data);
  const createManySpot = data => store.dispatch('Yard/createManySpot', data);
  const closeSpot = (id, data) => store.dispatch('Yard/closeSpot', { id, data });
  const openSpot = (id, data) => store.dispatch('Yard/openSpot', { id, data });
  const deleteSpot = id => store.dispatch('Yard/deleteSpot', id);
  const deleteManySpot = ids => store.dispatch('Yard/deleteManySpot', ids);
  const createSpotAssignment = data => store.dispatch('Yard/createSpotAssignment', data);
  const deleteSpotAssignment = id => store.dispatch('Yard/deleteSpotAssignment', id);
  const departSpotAssignment = (id, assetType) =>
    store.dispatch('Yard/departSpotAssignment', { id, assetType });
  const updateSpotAssignment = (id, data) =>
    store.dispatch('Yard/updateSpotAssignment', { id, data });
  const reassignSpotAssignment = (id, data) =>
    store.dispatch('Yard/reassignSpotAssignment', { id, data });

  /**
   * Executes spot reassignment, emits SPOT_ASSIGNMENT_CHANGED and tracks Mixpanel.
   * On failure (invalid or missing spotAssignment), notifies the user and returns null.
   * @param {string} spotAssignmentId - ID of the spot assignment to reassign
   * @param {Object} oldSpot - Current spot object (with spotAssignments)
   * @param {Object} newSpot - Target spot object
   * @param {{ reason?: string, entryPoint?: string }} options - reason and entryPoint for the action
   * @returns {Promise<Object|null>} The spotAssignment from the API, or null if invalid (caller notified).
   */
  const executeReassignSpotSelection = async (spotAssignmentId, oldSpot, newSpot, options = {}) => {
    const { reason = 'Drag and Drop', entryPoint = 'Drag and Drop' } = options;

    const spotAssignment = await reassignSpotAssignment(spotAssignmentId, {
      spotId: newSpot.id,
      oldSpotId: oldSpot.id,
      reason
    });

    if (!spotAssignment?.id) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    const isReassigningAssetVisit = isSpotAssignedToAssetVisit(oldSpot);

    eventHub.$emit(SPOT_ASSIGNMENT_CHANGED, {
      assetVisit: isReassigningAssetVisit
        ? spotAssignment.assetVisit ?? spotAssignment.assetContainer
        : spotAssignment.assetContainer,
      newSpotCode: spotAssignment.spot.code,
      newSpotAreaName: spotAssignment.spot.spotArea.name
    });

    trackMixpanelEvent(MixPanelEvent.REASSIGN, {
      spot: {
        ...newSpot,
        spotAssignments: oldSpot.spotAssignments?.[0] ? { ...oldSpot.spotAssignments[0] } : {}
      },
      entryPoint,
      assetVisit:
        oldSpot.spotAssignments?.[0]?.assetVisit ?? oldSpot.spotAssignments?.[0]?.assetContainer,
      metadata: {
        old_spot_id: oldSpot.id,
        old_spot_code: oldSpot.code
      }
    });

    return spotAssignment;
  };

  /**
   * Assigns an asset (assetVisit or assetContainer) to a spot. Creates or updates spot assignment,
   * emits SPOT_ASSIGNMENT_CHANGED, tracks Mixpanel and updates selectedSpotRef.
   * On failure (assignment create/update returns no id), notifies the user and returns null.
   * @param {Object} assetVisitOrContainer - The asset visit or asset container to assign
   * @param {string} spotId - ID of the target spot
   * @param {{ clearSelectedAssetVisit?: boolean, entryPoint?: string }} [options] - clearSelectedAssetVisit: clear selectedAssetVisitRef on success (e.g. detail panel); entryPoint: for Mixpanel (default 'Select from List')
   * @returns {Promise<Object|null>} The spotAssignment from the API, or null if invalid (caller notified on API failure)
   */
  const assignSpotToAsset = async (assetVisitOrContainer, spotId, options = {}) => {
    const { clearSelectedAssetVisit = false, entryPoint = 'Select from List' } = options;

    if (!assetVisitOrContainer?.id) return null;

    const spot = spots.value.find(s => s.id === spotId);
    if (!spot?.id) return null;

    if (isSpotReserved(spot)) {
      notify(t('yard.messages.thisSpotIsReservedForAnotherTask'), 'warning');
      return null;
    }

    if (isSpotClosed(spot)) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    const isAssetContainerSelected = isAssetContainer(assetVisitOrContainer);
    let spotAssignment = null;

    const assignmentPayload = {
      [isAssetContainerSelected ? 'assetContainerId' : 'assetVisitId']: assetVisitOrContainer.id,
      spotId: spot.id
    };

    if (isSpotOpen(spot)) {
      spotAssignment = await createSpotAssignment(assignmentPayload);
    } else {
      const isAssetContainerAssignedToVisitOnly =
        isAssetContainerSelected && isSpotAssignedOnlyToAssetVisit(spot);
      const isVisitAssignedToContainerOnly =
        isSpotAssignedOnlyToAssetContainer(spot) && !isAssetContainerSelected;
      const isSpotFullyOccupied =
        isSpotAssignedToAssetVisit(spot) && isSpotAssignedToAssetContainer(spot);

      if (
        !isSpotFullyOccupied &&
        (isAssetContainerAssignedToVisitOnly || isVisitAssignedToContainerOnly)
      ) {
        const existingAssignmentId = spot.spotAssignments?.[0]?.id;
        if (existingAssignmentId) {
          spotAssignment = await updateSpotAssignment(existingAssignmentId, assignmentPayload);
        } else {
          spotAssignment = await createSpotAssignment(assignmentPayload);
        }
      } else {
        notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
        return null;
      }
    }

    if (!spotAssignment?.id) {
      notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
      return null;
    }

    eventHub.$emit(SPOT_ASSIGNMENT_CHANGED, {
      assetVisit: assetVisitOrContainer,
      newSpotCode: spot.code,
      newSpotAreaName: spotAreas.value.find(area => area.id === spot.spotAreaId)?.name
    });

    trackMixpanelEvent(MixPanelEvent.ASSIGNED, {
      spot,
      assetVisit: assetVisitOrContainer,
      entryPoint
    });

    selectedSpot.value = spot;
    if (clearSelectedAssetVisit) {
      selectedAssetVisit.value = null;
    }

    return spotAssignment;
  };

  const messageDriver = (assetVisitId, content) =>
    store.dispatch('Yard/messageDriver', { assetVisitId, content });
  const setIsLoadingSpots = value => store.commit('Yard/setIsLoadingSpots', value);
  const setIsLoadingAssetVisits = value => store.commit('Yard/setIsLoadingAssetVisits', value);

  // Subspace
  const showUpdateDataFromSubspace = (event, collection) => {
    return (
      collection?.value?.find(item => item.id === event.id) ||
      event.warehouseId === selectedWarehouse.value?.id
    );
  };
  const handleAssetContainerEventSubspaceEvent = async event => {
    const containerSpot = spots.value.find(spot => {
      const spotAssignment = spot.spotAssignments[0];
      return (
        spotAssignment?.assetContainer?.id === event.assetContainerId ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.id === event.assetContainerId)
          ?.id
      );
    });

    const isDeparted = event.eventType === AssetContainerEventType.Departed;
    // Same logic as asset visit: add event when Departed (so waiting list stays correct
    // if spot-assignment delete is processed first) or when container is not on a spot.
    const shouldAddEvent = isDeparted || !containerSpot;
    if (shouldAddEvent) {
      store.commit('Yard/addAssetContainerEvent', {
        assetContainerId: event.assetContainerId,
        event
      });
    }

    // waitingAssetVisits only checks assetVisitEvents, not assetContainerEvents,
    // so we must explicitly remove departed containers (standalone or nested)
    if (isDeparted) {
      store.commit('Yard/removeAssetVisit', event.assetContainerId);
      store.commit('Yard/removeAssetContainerFromVisit', {
        assetContainerId: event.assetContainerId
      });
    }

    if (containerSpot) {
      return fetchSpot(containerSpot.id).then(updateSpotOnSpotList);
    }
  };
  const handleAssetContainerSubspaceEvent = async event => {
    // Hard-delete payloads may lack warehouseId; handle them first
    if (event.action === 'delete') {
      store.commit('Yard/removeAssetVisit', event.id);
      store.commit('Yard/removeAssetContainerFromVisit', { assetContainerId: event.id });
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = null;
      }
      return;
    }

    if (!selectedWarehouse.value?.id || event.warehouseId !== selectedWarehouse.value.id) {
      return;
    }

    for (const spot of spots.value) {
      const spotAssignment = spot.spotAssignments[0];
      if (
        spotAssignment?.assetContainer?.id === event.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.id === event.id)?.id
      ) {
        return fetchSpot(spot.id).then(updateSpotOnSpotList);
      }
    }

    // Optimistic update for containers in the waiting list
    if (event.isActive === false) {
      store.commit('Yard/removeAssetVisit', event.id);
      store.commit('Yard/removeAssetContainerFromVisit', { assetContainerId: event.id });
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = null;
      }
      return;
    }

    // New container: fetch full entity with nested data before deciding placement
    if (event.action === 'create') {
      try {
        const assetContainer = await store.dispatch('Yard/fetchAndUpsertAssetContainer', event.id);

        if (!assetContainer) {
          console.error('Failed to fetch asset container on create event:', event.id);
          return;
        }

        // If created already attached to a visit, relocate into the parent
        if (assetContainer.assetVisitId) {
          store.commit('Yard/removeAssetVisit', assetContainer.id);
          store.commit('Yard/addAssetContainerToVisit', {
            assetVisitId: assetContainer.assetVisitId,
            assetContainer
          });
        }

        eventHub.$emit(NEW_WAITING_ARRIVAL);
      } catch (error) {
        console.error('Failed to fetch asset container on create event:', error);
      }
      return;
    }

    // Container attached to an asset visit is no longer standalone;
    // remove from top-level and inject into the parent visit's assetContainers
    if (event.assetVisitId) {
      store.commit('Yard/removeAssetVisit', event.id);
      store.commit('Yard/addAssetContainerToVisit', {
        assetVisitId: event.assetVisitId,
        assetContainer: event
      });
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = null;
      }
      return;
    }

    // Update: merge sparse WS flat fields into existing rich store data
    store.commit('Yard/upsertAssetVisit', event);

    // Sync the detail view
    if (selectedAssetVisit.value?.id === event.id) {
      selectedAssetVisit.value = { ...selectedAssetVisit.value, ...event };
    }
  };
  const updateSpotOnSpotList = spot => {
    if (!spot?.id) {
      return;
    }

    if (spots.value.map(s => s.id).includes(spot.id)) {
      store.commit(
        'Yard/setSpots',
        spots.value.map(s => (s.id === spot.id ? spot : s))
      );
    } else {
      store.commit('Yard/setSpots', [...spots.value, spot]);
    }

    if (selectedSpot.value?.id === spot.id) {
      store.commit('Yard/setSelectedSpot', spot);
    }
  };
  const handleAssetVisitEventSubspaceEvent = async event => {
    const assetVisitSpot = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.assetVisitId === event.assetVisitId)
    );

    const isDepartedOrCanceled =
      event.eventType === AssetVisitEventType.Departed ||
      event.eventType === AssetVisitEventType.Canceled;

    // Add event when: Departed/Canceled (so waiting list filters it out even if
    // spot-assignment delete is processed first), or when visit is not on a spot.
    const shouldAddEvent = isDepartedOrCanceled || !assetVisitSpot;
    if (shouldAddEvent) {
      store.commit('Yard/addAssetVisitEvent', {
        assetVisitId: event.assetVisitId,
        event
      });
    }

    if (assetVisitSpot) {
      return fetchSpot(assetVisitSpot.id).then(updateSpotOnSpotList);
    }
  };
  const handleAssetVisitSubspaceEvent = async event => {
    // Hard-delete payloads may lack warehouseId; handle them if the entity
    // exists in our local data (waiting list or spots)
    if (event.action === 'delete') {
      store.commit('Yard/removeAssetVisit', event.id);
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = null;
      }
      return;
    }

    if (!selectedWarehouse.value?.id || event.warehouseId !== selectedWarehouse.value.id) {
      return;
    }

    const assetVisitSpot = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.assetVisitId === event.id)
    );

    if (assetVisitSpot) {
      return fetchSpot(assetVisitSpot.id).then(updateSpotOnSpotList);
    }

    // Optimistic update for the waiting list (no full reload)
    if (event.isActive === false) {
      store.commit('Yard/removeAssetVisit', event.id);
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = null;
      }
      return;
    }

    if (!isAssetVisitWithinFetchWindow(event)) {
      return;
    }

    if (event.action === 'create') {
      // Fetch the full entity with all nested data (company, appointment, etc.)
      try {
        await store.dispatch('Yard/fetchAndUpsertAssetVisit', event.id);
        eventHub.$emit(NEW_WAITING_ARRIVAL);
      } catch (error) {
        console.error('Failed to fetch asset visit on create event:', error);
        // Fallback: use the websocket payload to keep local state consistent
        store.commit('Yard/upsertAssetVisit', event);
      }
    } else {
      store.commit('Yard/upsertAssetVisit', event);

      // Sync the detail view: merge websocket flat fields into the existing
      // selectedAssetVisit to preserve richer nested data (company, appointment)
      // that the websocket payload does not carry
      if (selectedAssetVisit.value?.id === event.id) {
        selectedAssetVisit.value = { ...selectedAssetVisit.value, ...event };
      }
    }
  };
  const handleSpotAreaSubspaceEvent = async event => {
    if (showUpdateDataFromSubspace(event, spotAreas)) {
      fetchSpotAreas();
    }
  };
  const handleSpotSubspaceEvent = async event => {
    if (event.debounce?.count > 0) {
      return fetchSpots().then(() => {
        if (event.debounce.ids?.includes(selectedSpot.value?.id)) {
          store.commit('Yard/setSelectedSpot', {});
        }
      });
    }

    if (showUpdateDataFromSubspace(event, spots)) {
      if (event.action === 'delete') {
        store.commit(
          'Yard/setSpots',
          spots.value.filter(s => s.id !== event.id)
        );

        if (selectedSpot.value?.id === event.id) {
          store.commit('Yard/setSelectedSpot', {});
        }
        return;
      }
      return fetchSpot(event.id).then(updateSpotOnSpotList);
    }
  };
  const handleSpotAssignmentSubspaceEvent = async event => {
    if (event.debounce?.count > 0) {
      // Spot updates are enough — the waitingAssetVisits computed
      // checks spots.value for assigned IDs
      fetchSpots();
      return;
    }

    if (event.action === 'delete') {
      const spotId = event.spotId;

      // Spot update removes the assignment from spots.value,
      // which lets the waitingAssetVisits computed pick up the unassigned visit
      return fetchSpot(spotId).then(updateSpotOnSpotList);
    }

    const existingSpotAssignment = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.id === event.id)
    );
    const newSpotId = event.spotId;

    if (existingSpotAssignment) {
      fetchSpot(existingSpotAssignment.id).then(updateSpotOnSpotList);
    }

    if (newSpotId) {
      fetchSpot(newSpotId).then(updateSpotOnSpotList);
    }
  };
  const handleAppointmentSubspaceEvent = event => {
    for (const spot of spots.value) {
      const spotAssignment = spot.spotAssignments[0];
      if (
        spotAssignment?.assetVisit?.appointmentId === event.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(
          ac => ac.pickupAppointment?.id === event.id
        )?.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.dropAppointment?.id === event.id)
          ?.id ||
        spotAssignment?.assetContainer?.pickupAppointment?.id === event.id ||
        spotAssignment?.assetContainer?.dropAppointment?.id === event.id
      ) {
        fetchSpot(spot.id).then(updateSpotOnSpotList);
      }
    }

    // Optimistic update: the mutation searches all appointment locations
    // (visit.appointment, container.pickupAppointment, container.dropAppointment,
    // and nested containers) so we don't set the wrong field
    store.commit('Yard/updateAppointmentInWaitingList', {
      appointmentId: event.id,
      appointment: event
    });

    // Refresh selectedAssetVisit if affected
    for (const assetVisit of waitingAssetVisits.value) {
      if (
        selectedAssetVisit.value?.id === assetVisit.id &&
        (assetVisit.appointmentId === event.id ||
          assetVisit.pickupAppointment?.id === event.id ||
          assetVisit.dropAppointment?.id === event.id ||
          assetVisit.assetContainers?.find(ac => ac.pickupAppointment?.id === event.id)?.id ||
          assetVisit.assetContainers?.find(ac => ac.dropAppointment?.id === event.id)?.id)
      ) {
        selectedAssetVisit.value = waitingAssetVisits.value.find(av => av.id === assetVisit.id);
        break;
      }
    }
  };

  const handleUpdateTaskEvent = debounce(async () => {
    await store.dispatch('Yard/fetchActiveMoveTasks');
  }, 300);

  const validateSpotCodeInput = value => {
    if (
      value?.length < 1 ||
      value?.length > YARD_SPOT_CODE_MAX_LENGTH ||
      !/^[a-zA-Z0-9]*$/.test(value)
    ) {
      return `Code must be between 1 and ${YARD_SPOT_CODE_MAX_LENGTH} characters and contains only letters and numbers (a-z,  0-9)`;
    }
    return true;
  };

  const validateSpotTypeInput = value => {
    if (!value) {
      return 'Spot type is required';
    }

    if (!Object.values(SpotType).includes(value)) {
      return `Spot type is not valid, valid choices are ${Object.values(SpotType).join(',')}`;
    }
    return true;
  };

  const formatDateTime = (date, amPmFormat, militaryFormat) =>
    formatDateTimeWithMilitarySupport(
      date,
      selectedWarehouse.value?.timezone,
      amPmFormat,
      isMilitaryTimeEnabled.value,
      militaryFormat,
      locale.value
    );

  const getTranslatedCompanyName = companyName => {
    return companyName ?? t('yard.messages.unknownCompany');
  };

  const getTranslatedAssetContainerType = type => {
    return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
  };

  const getTranslatedSpotStatus = spot => t(`core.yard.spotStatuses.${camelCase(spot.status)}`);

  const spotsByAreaMap = computed(() => {
    const spotsByArea = new Map();

    spotAreas.value.forEach(area => {
      spotsByArea.set(area.id, {
        area,
        spots: []
      });
    });

    spots.value.forEach(spot => {
      if (!spot.isActive) {
        return;
      }

      const areaData = spotsByArea.get(spot.spotAreaId);
      if (areaData) {
        areaData.spots.push(spot);
      } else {
        console.warn('Could not find area', spot.spotAreaId, { spot: JSON.stringify(spot) });
      }
    });

    return spotsByArea;
  });

  const getSpotsByArea = areaId => {
    return spotsByAreaMap.value.get(areaId)?.spots || [];
  };

  const isSpotOpen = spot => spot.status === SpotStatus.OPEN;
  const isSpotClosed = spot => spot.status === SpotStatus.CLOSED;
  const isSpotAssigned = spot => spot.status === SpotStatus.ASSIGNED;
  const isSpotReserved = spot => SPOT_STATUSES_RESERVED.includes(spot.status);

  /**
   * TODO: evaluate if we need to increase the granularity of this getter
   * to return if the spot is the origin or destination of an active move task,
   * or the actual active move task itself.
   *
   * If we do, we need to update the `useYardSpotStatus` composable to use the new getter,
   * or remove it altogether.
   */
  const isSpotInActiveMoveTask = spotId => {
    return store.getters['Yard/isSpotInActiveMoveTask'](spotId);
  };

  const hasAssignedAssetContainer = spot => {
    const toArray = value => (Array.isArray(value) ? value : []);
    const spotAssignments = toArray(spot?.spotAssignments);

    return spotAssignments.some(spotAssignment => {
      if (spotAssignment.assetContainer?.id) {
        return true;
      }

      if (toArray(spotAssignment.assetVisit?.assetContainers).some(ac => ac.id)) {
        return true;
      }
      return false;
    });
  };

  const doesSpotHaveLockedAsset = spot => {
    /**
     * We check this because, the origin spot for a move task - which is causes
     * an asset to be "locked" - is currently being set as closed (i.e., isActive = false).
     */
    if (!isSpotClosed(spot)) {
      return false;
    }

    return hasAssignedAssetContainer(spot);
  };

  // Give then customFormData API response, asset container id and form field ID from the triggers.
  // Find the formatted value based on the form template inside the customFormData.
  const getCustomFormFieldValue = (customFormData, assetUUID, formFieldId) => {
    if (!customFormData?.length) {
      return null;
    }

    const formData = customFormData.find(
      formData => formData.objectId === assetUUID && formData.formField?.id === formFieldId
    );

    if (isDefined(formData?.value)) {
      if ([CustomFieldType.Date, CustomFieldType.Timestamp].includes(formData.type)) {
        // Keep date/time fields as is, so we can sort them properly later
        return formData.value;
      }

      return getFormattedValue(formData);
    }

    return null;
  };

  return {
    // Read-Write Refs
    selectedWarehouseRef: selectedWarehouse,
    selectedAreasRef: selectedAreas,
    selectedSpotRef: selectedSpot,
    selectedAssetVisitRef: selectedAssetVisit,
    selectedSpotsRef: selectedSpots,
    viewModeRef: viewMode,
    isAssetVisitChatVisibleRef: isAssetVisitChatVisible,
    // Read-Only Refs
    assetContainerTriggersRef: assetContainerTriggers,
    spotAreasRef: spotAreas,
    spotsRef: spots,
    assetContainersRef: assetContainers,
    isLoadingSpotsRef: isLoadingSpots,
    isLoadingAssetVisitsRef: isLoadingAssetVisits,
    defaultWarehouseIdRef: defaultWarehouseId,
    defaultSpotAreaIdRef: defaultSpotAreaId,
    defaultSpotIdRef: defaultSpotId,
    waitingAssetVisitsRef: waitingAssetVisits,
    // Methods
    fakeCheckIn,
    fakeCheckInUnplanned,
    attachAssetVisit,
    detachAssetVisit,
    updateAssetContainerStatus,
    updateAssetVisitStatus,
    getAppointmentById,
    getAssetVisitSpot,
    fetchSpots,
    fetchSpotAreas,
    fetchAssetVisits,
    fetchAndUpsertAssetVisit,
    fetchAndUpsertAssetContainer,
    fetchAssetContainers,
    createSpotArea,
    updateSpotArea,
    deleteSpotArea,
    getArrivedAssetContainerEvent,
    getArrivedAssetContainerCompanyName,
    handleAssetVisitEventSubspaceEvent,
    handleAssetVisitSubspaceEvent,
    handleSpotAreaSubspaceEvent,
    handleSpotSubspaceEvent,
    handleAssetContainerSubspaceEvent,
    handleAssetContainerEventSubspaceEvent,
    handleUpdateTaskEvent,
    getAppointmentSpot,
    createSpot,
    createManySpot,
    closeSpot,
    openSpot,
    deleteSpot,
    deleteManySpot,
    fetchSpot,
    updateSpot,
    messageDriver,
    fetchPreviousYardData,
    fetchAssetContainerTriggers,
    createSpotAssignment,
    deleteSpotAssignment,
    departSpotAssignment,
    reassignSpotAssignment,
    executeReassignSpotSelection,
    assignSpotToAsset,
    updateSpotAssignment,
    dropHandled,
    setDropHandled,
    dragOriginSpotIdRef,
    setDragOriginSpotId,
    validateSpotCodeInput,
    validateSpotTypeInput,
    handleAppointmentSubspaceEvent,
    handleSpotAssignmentSubspaceEvent,
    getAssetVisitTypeDisplayName,
    formatDateTime,
    getTruckThemeColor,
    setIsLoadingSpots,
    isEditableSpotSelected,
    isReadonlyAccess,
    sortSpots,
    relativeTimeFromWarehouse,
    getAssignedAssetContainerStatus,
    setIsLoadingAssetVisits,
    updateAssetContainer,
    isAssetContainer,
    updateSpotOnSpotList,
    trackMixpanelEvent,
    scheduledFor,
    MixPanelEvent,
    getCustomFormFieldValue,
    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    isSpotInActiveMoveTask,
    hasAssetVisitOnSpot: spot => isSpotAssignedToAssetVisit(spot),
    hasAssetContainerOnSpot: spot => isSpotAssignedToAssetContainer(spot),
    hasAssetVisitOnlyOnSpot: spot => isSpotAssignedOnlyToAssetVisit(spot),
    hasAssetContainerOnlyOnSpot: spot => isSpotAssignedOnlyToAssetContainer(spot),
    // Basic Data
    spotCardHeight,
    spotCardWidth,
    spotType: SpotType,
    spotStatus: SpotStatus,
    persistSelectedWarehouse,
    persistListViewColumns,
    persistWaitingSortOrder,
    persistShowWaitingAssets,
    persistSelectedAreas,
    persistExpandedAreaPanels,
    getAssignedSpotIcon,
    getAssetContainerStateIcon,
    getAssetContainerStatusIcon,
    getAssetContainerStatusColor,
    getSpotIcon,
    getTruckIcon,
    getLoadTypeDirectionColor,
    getContainerSpotAssignmentText,
    getAppointmentFieldValue,
    timeElapsedFromWarehouse,
    persistListViewCustomColumns,
    isYMSEnabledForSelectedWarehouse,
    isGateManagementEnabledForSelectedWarehouse,
    getEvents,
    getLatestClosedEvent,
    doesSpotHaveLockedAsset,
    cannotCreateSpotAssignmentRef: cannotCreateSpotAssignment,
    canManageSpotsAndAreasRef: canManageSpotsAndAreas,
    canEditAssetFormsRef: canEditAssetForms,
    canEditAssetContainerDataRef: canEditAssetContainerData,
    getSpotStatus,
    isMilitaryTimeEnabled,
    goToSpotFromOtherModules,
    truckThemeColors,
    YARD_SPOT_CODE_MAX_LENGTH,
    SPOT_ASSIGNMENT_CHANGED,
    GO_TO_SPOT,
    NEW_WAITING_ARRIVAL,
    getTranslatedCompanyName,
    getTranslatedAssetContainerType,
    getTranslatedSpotStatus,
    spotsByAreaMap,
    getSpotsByArea
  };
}
